import { useCallback, useEffect, useRef, useState } from "react";

/** @returns {boolean} */
function chatbotEnabled() {
  const raw = process.env.REACT_APP_CHATBOT_WIDGET_URL;
  if (typeof raw === "string") {
    return raw.trim() !== "";
  }
  if (process.env.NODE_ENV === "development") {
    return true;
  }
  return false;
}

function chatbotId() {
  const raw = process.env.REACT_APP_CHATBOT_ID;
  if (typeof raw !== "string") return "halalin";
  const t = raw.trim();
  return t !== "" ? t : "halalin";
}

function storageKey() {
  return `halalin_conv_${chatbotId()}`;
}

/** Chatbot HTTP origin in dev, or "" to use same-origin `/chatbot` prefix in production. */
function chatbotApiOrigin() {
  const raw = process.env.REACT_APP_CHATBOT_API_BASE;
  if (typeof raw === "string" && raw.trim() !== "") {
    return raw.trim().replace(/\/$/, "");
  }
  if (process.env.NODE_ENV === "development") {
    const port = process.env.REACT_APP_CHATBOT_DEV_PORT || "8082";
    return `http://127.0.0.1:${port}`;
  }
  return "";
}

/** @param {string} suffix path starting with /api/… */
function chatbotUrl(suffix) {
  const o = chatbotApiOrigin();
  if (o) return `${o}${suffix}`;
  return `/chatbot${suffix}`;
}

/**
 * Same-origin chat UI (no iframe): floating bubble + panel calling POST /api/v1/chat.
 * Production: requests go to `/chatbot/api/v1/...` via nginx (see nginx.conf).
 * Visibility matches the old embed: dev on by default; prod requires non-empty REACT_APP_CHATBOT_WIDGET_URL at build time.
 */
export function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(
    /** @type {{ role: string, content: string }[]} */ ([]),
  );
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState("");
  const listRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, open, sending, scrollToBottom]);

  useEffect(() => {
    if (!chatbotEnabled()) return undefined;
    const key = storageKey();
    const stored = window.localStorage.getItem(key);
    if (!stored) return undefined;
    setConversationId(stored);
    let cancelled = false;
    fetch(chatbotUrl(`/api/v1/conversations/${encodeURIComponent(stored)}`), {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("load"))))
      .then((data) => {
        if (cancelled || !data?.messages?.length) return;
        setMessages(
          data.messages.map((m) => ({
            role: m.role,
            content: String(m.content || ""),
          })),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const startNewChat = useCallback(() => {
    const id = conversationId;
    if (id) {
      fetch(chatbotUrl(`/api/v1/conversations/${encodeURIComponent(id)}`), {
        method: "DELETE",
      }).catch(() => {});
    }
    window.localStorage.removeItem(storageKey());
    setConversationId("");
    setMessages([]);
  }, [conversationId]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setSending(true);
    try {
      const key = storageKey();
      const body = JSON.stringify({
        conversation_id: conversationId || window.localStorage.getItem(key) || "",
        message: text,
      });
      const res = await fetch(chatbotUrl("/api/v1/chat"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = data?.error ? String(data.error) : `HTTP ${res.status}`;
        const code = data?.code ? ` (${data.code})` : "";
        setMessages((prev) => [...prev, { role: "assistant", content: err + code }]);
        return;
      }
      const nextId = data?.conversation_id;
      if (nextId) {
        setConversationId(nextId);
        window.localStorage.setItem(key, nextId);
      }
      const reply = data?.reply != null ? String(data.reply) : "";
      if (!reply.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Tidak ada balasan dari model. Cek log server, kunci API OpenAI, dan CHAT_MODEL.",
          },
        ]);
        return;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Terjadi kesalahan. Silakan coba lagi." },
      ]);
    } finally {
      setSending(false);
    }
  }, [conversationId, input, sending]);

  if (!chatbotEnabled()) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        id="halalin-chat-bubble"
        name="halalin-chat-open"
        aria-label={open ? "Tutup chat" : "Buka chat"}
        aria-expanded={open}
        className="fixed bottom-6 right-6 z-[999999] flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-0 bg-[#1C554C] text-2xl text-white shadow-lg"
        onClick={() => setOpen((v) => !v)}
      >
        💬
      </button>

      {open ? (
        <div
          id="halalin-chat-panel"
          className="fixed bottom-[5.5rem] right-6 z-[999998] flex h-[560px] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl bg-[#f8fafc] shadow-2xl"
          role="dialog"
          aria-label="Halli chatbot"
        >
          <header className="flex shrink-0 flex-col gap-1 bg-[#1C554C] px-4 py-3 text-white">
            <div className="flex w-full items-center justify-between gap-2">
              <div>
                <p className="text-[15px] font-bold">Halli 🤖</p>
                <p className="text-[11px] font-normal opacity-75">
                  Asisten Sertifikasi Halal · Halalin
                </p>
              </div>
              <button
                type="button"
                id="halalin-chatbot-new"
                name="halalin-chatbot-new"
                className="cursor-pointer rounded-lg border border-white/60 bg-transparent px-3 py-1.5 text-xs text-white"
                onClick={startNewChat}
              >
                Mulai Baru
              </button>
            </div>
          </header>

          <div
            ref={listRef}
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
          >
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div
                  key={`${i}-${m.content.slice(0, 20)}`}
                  className="max-w-[85%] self-end rounded-xl bg-[#1C554C] px-3.5 py-2.5 text-sm leading-relaxed text-white"
                >
                  {m.content}
                </div>
              ) : (
                <div
                  key={`${i}-${m.content.slice(0, 20)}`}
                  className="max-w-[85%] self-start whitespace-pre-wrap rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800"
                >
                  {m.content}
                </div>
              ),
            )}
            {sending ? (
              <div className="flex max-w-[85%] items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm italic text-slate-500">
                <span className="flex gap-1">
                  <span
                    className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1C554C] [animation-delay:-0.2s]"
                    aria-hidden
                  />
                  <span
                    className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1C554C] [animation-delay:-0.1s]"
                    aria-hidden
                  />
                  <span
                    className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1C554C]"
                    aria-hidden
                  />
                </span>
                Halli lagi mikir...
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 gap-2 border-t border-slate-200 bg-white p-3">
            <input
              id="halalin-chatbot-input"
              name="halalin-chatbot-message"
              type="text"
              autoComplete="off"
              placeholder="Ketik pertanyaan..."
              value={input}
              disabled={sending}
              className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#1C554C]/30"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
            />
            <button
              type="button"
              id="halalin-chatbot-send"
              name="halalin-chatbot-send"
              disabled={sending}
              className="shrink-0 cursor-pointer rounded-lg border-0 bg-[#FFC617] px-4 py-2.5 text-sm font-medium text-[#282828] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={send}
            >
              Kirim
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
